import React, { useEffect, useState } from "react";
import { MdOutlineAccessTime } from "react-icons/md";
import Toast from "../../../../Components/Toast";
import useAuthStore from "../../../../store/useAuthStore";

const Overtime = () => {
  const { token } = useAuthStore();
  const today = new Date();
  const defaultMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;

  const [month, setMonth] = useState(defaultMonth);
  const [employees, setEmployees] = useState([]);
  const [overtimeData, setOvertimeData] = useState({}); // { empId: { hours, rate } }
  const [bulkRate, setBulkRate] = useState("");
  const [savingAll, setSavingAll] = useState(false);
  const [saving, setSaving] = useState({}); // { empId: true/false }
  const [toast, setToast] = useState(null);

  // Fetch employees
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const res = await fetch("/api/getallEmployees", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (res.ok) {
          setEmployees(
            data.employees.map((e) => ({
              id: e._id,
              name: e.name,
              department: e.department || "—",
            }))
          );
        }
      } catch (err) {
        console.error("Error fetching employees:", err);
      }
    };
    fetchEmployees();
  }, [token]);

  // Fetch existing overtime records whenever month changes
  useEffect(() => {
    if (!month) return;
    const fetchOvertime = async () => {
      try {
        const res = await fetch(`/api/overtime?month=${month}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (res.ok && Array.isArray(data.overtime)) {
          const map = {};
          data.overtime.forEach((ot) => {
            const empId =
              typeof ot.employeeId === "object"
                ? ot.employeeId._id || ot.employeeId
                : ot.employeeId;
            map[String(empId)] = { hours: ot.hours, rate: ot.rate };
          });
          setOvertimeData(map);
        }
      } catch (err) {
        console.error("Error fetching overtime:", err);
      }
    };
    fetchOvertime();
  }, [month, token]);

  const getRow = (empId) =>
    overtimeData[empId] || { hours: "", rate: "" };

  const updateRow = (empId, field, value) => {
    setOvertimeData((prev) => ({
      ...prev,
      [empId]: { ...getRow(empId), [field]: value },
    }));
  };

  const applyBulkRate = () => {
    if (!bulkRate && bulkRate !== 0) return;
    setOvertimeData((prev) => {
      const next = { ...prev };
      employees.forEach((e) => {
        next[e.id] = { ...getRow(e.id), rate: bulkRate };
      });
      return next;
    });
  };

  const saveRow = async (empId) => {
    const row = getRow(empId);
    setSaving((prev) => ({ ...prev, [empId]: true }));
    try {
      const res = await fetch("/api/overtime", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          employeeId: empId,
          month,
          hours: Number(row.hours) || 0,
          rate: Number(row.rate) || 0,
        }),
      });
      if (res.ok) {
        setToast({ type: "success", title: "Saved", message: "Overtime saved." });
      } else {
        const d = await res.json();
        setToast({ type: "error", title: "Error", message: d.error || "Failed to save." });
      }
    } catch {
      setToast({ type: "error", title: "Error", message: "Network error." });
    }
    setSaving((prev) => ({ ...prev, [empId]: false }));
  };

  const saveAll = async () => {
    setSavingAll(true);
    const records = employees
      .filter((e) => {
        const row = getRow(e.id);
        return Number(row.hours) > 0;
      })
      .map((e) => {
        const row = getRow(e.id);
        return {
          employeeId: e.id,
          month,
          hours: Number(row.hours) || 0,
          rate: Number(row.rate) || 0,
        };
      });

    if (records.length === 0) {
      setToast({ type: "error", title: "Nothing to save", message: "Enter overtime hours for at least one employee." });
      setSavingAll(false);
      return;
    }

    try {
      const res = await fetch("/api/overtime/bulk", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ records }),
      });
      if (res.ok) {
        setToast({ type: "success", title: "Saved", message: `${records.length} overtime record(s) saved.` });
      } else {
        const d = await res.json();
        setToast({ type: "error", title: "Error", message: d.error || "Failed to save." });
      }
    } catch {
      setToast({ type: "error", title: "Error", message: "Network error." });
    }
    setSavingAll(false);
  };

  return (
    <div className="p-6">
      {toast && (
        <Toast
          type={toast.type}
          title={toast.title}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}

      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <MdOutlineAccessTime className="text-2xl text-primary" />
        <div>
          <h1 className="text-xl font-bold text-gray-800">Overtime Calculator</h1>
          <p className="text-sm text-gray-500">
            Record overtime hours per employee. This will be included as an allowance when generating payslips for the selected month.
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-end gap-4 mb-6">
        {/* Month picker */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Month</label>
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* Bulk rate setter */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Bulk Hourly Rate (Nu.)</label>
          <div className="flex gap-2">
            <input
              type="number"
              min="0"
              value={bulkRate}
              onChange={(e) => setBulkRate(e.target.value)}
              placeholder="e.g. 150"
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-32 focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <button
              onClick={applyBulkRate}
              className="bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-600 transition"
            >
              Apply to All
            </button>
          </div>
        </div>

        {/* Save All button */}
        <button
          onClick={saveAll}
          disabled={savingAll}
          className="ml-auto bg-green-500 text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-green-600 transition disabled:opacity-50"
        >
          {savingAll ? "Saving…" : "Save All"}
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-3 font-semibold text-gray-600">#</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-600">Employee</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-600">Department</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-600">Overtime Hours</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-600">Hourly Rate (Nu.)</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-600">Amount (Nu.)</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-600">Action</th>
            </tr>
          </thead>
          <tbody>
            {employees.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center py-8 text-gray-400">
                  No employees found.
                </td>
              </tr>
            ) : (
              employees.map((emp, idx) => {
                const row = getRow(emp.id);
                const hours = Number(row.hours) || 0;
                const rate = Number(row.rate) || 0;
                const amount = Math.round(hours * rate);
                return (
                  <tr
                    key={emp.id}
                    className={`border-b border-gray-100 ${idx % 2 === 0 ? "bg-white" : "bg-gray-50"} hover:bg-blue-50 transition`}
                  >
                    <td className="px-4 py-3 text-gray-500">{idx + 1}</td>
                    <td className="px-4 py-3 font-medium text-gray-800">{emp.name}</td>
                    <td className="px-4 py-3 text-gray-500">{emp.department}</td>
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        min="0"
                        value={row.hours}
                        onChange={(e) => updateRow(emp.id, "hours", e.target.value)}
                        placeholder="0"
                        className="border border-gray-300 rounded-lg px-2 py-1 w-24 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <span className="text-gray-500 text-xs">Nu.</span>
                        <input
                          type="number"
                          min="0"
                          value={row.rate}
                          onChange={(e) => updateRow(emp.id, "rate", e.target.value)}
                          placeholder="0"
                          className="border border-gray-300 rounded-lg px-2 py-1 w-24 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                    </td>
                    <td className="px-4 py-3 font-semibold text-green-700">
                      {amount > 0 ? `Nu. ${amount.toLocaleString("en-IN")}` : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => saveRow(emp.id)}
                        disabled={saving[emp.id]}
                        className="bg-primary text-white px-3 py-1 rounded-lg text-xs font-medium hover:bg-blue-600 transition disabled:opacity-50"
                      >
                        {saving[emp.id] ? "Saving…" : "Save"}
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {employees.length > 0 && (
        <p className="mt-3 text-xs text-gray-400">
          * Overtime will be automatically added as an allowance when payslips are generated for {month}.
        </p>
      )}
    </div>
  );
};

export default Overtime;
